import { useCallback, useEffect, useRef } from "react";
import { useRequest } from "hooks/useRequest";
import api from "utils/api";
import type {
  UseSeenFloorPlanElementsArgs,
  UseSeenFloorPlanElementsResult,
} from "./types";

const EMPTY_SET = new Set<string>();

export const useSeenFloorPlanElements = ({
  elementTypeId,
}: UseSeenFloorPlanElementsArgs): UseSeenFloorPlanElementsResult => {
  const cacheRef = useRef<Map<number, Set<string>>>(new Map());

  const fetchSeenIds = useCallback(
    async (id: number): Promise<Set<string>> => {
      const cached = cacheRef.current.get(id);
      if (cached) return cached;

      const response: { markerIds: number[] } =
        await api.getSeenMarkerIds(id);
      const ids = new Set(response.markerIds.map(String));
      cacheRef.current.set(id, ids);
      return ids;
    },
    [],
  );

  const { data, isLoading, run, mutateData } = useRequest(fetchSeenIds, {
    manual: true,
  });

  const seenIds = elementTypeId ? (data ?? EMPTY_SET) : EMPTY_SET;

  const markSeen = useCallback(
    (markerId: string) => {
      mutateData({
        updater: (prev) => {
          if (prev.has(markerId)) return prev;
          const next = new Set(prev);
          next.add(markerId);

          if (elementTypeId) {
            cacheRef.current.set(elementTypeId, next);
          }

          return next;
        },
      });

      const numericId = parseInt(markerId, 10);
      if (!isNaN(numericId)) {
        api.markMarkerSeen(numericId).catch(() => {
          // Best-effort — marking seen is not critical
        });
      }
    },
    [elementTypeId, mutateData],
  );

  useEffect(() => {
    if (!elementTypeId) return;
    run(elementTypeId);
  }, [elementTypeId, run]);

  return { seenIds, isLoading, markSeen };
};
